import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import {
  Box,
  Dialog,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { NAV_ITEMS_FLAT } from '@/config/navigation';
import { usePermissions } from '@/auth/permissions';

/**
 * Command palette + global search. Opens with ⌘K / Ctrl-K or the search button,
 * filters the permission-aware navigation, and jumps to the chosen destination
 * (spec §4). Keyboard: arrows to move, Enter to go, Esc to close.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const { can } = usePermissions();

  const commands = useMemo(
    () =>
      NAV_ITEMS_FLAT.filter(
        (item) => !item.permission || can(item.permission, item.permissionMode ?? 'all'),
      ),
    [can],
  );

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands.slice(0, 8);
    return commands.filter((c) => c.label.toLowerCase().includes(q)).slice(0, 12);
  }, [commands, query]);

  useEffect(() => {
    function onKey(e: globalThis.KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
    }
  }, [open]);

  const commit = (index: number) => {
    const target = results[index];
    if (target) {
      setOpen(false);
      navigate(target.path);
    }
  };

  const onListKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commit(active);
    }
  };

  return (
    <>
      {/* Desktop: search-field affordance. */}
      <Box
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setOpen(true)}
        aria-label="Open command palette"
        sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          minWidth: 260,
          borderRadius: 2,
          cursor: 'pointer',
          color: 'text.secondary',
          border: (t) => `1px solid ${t.palette.divider}`,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <SearchIcon fontSize="small" />
        <Typography variant="body2" sx={{ flexGrow: 1 }}>
          Search…
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          ⌘K
        </Typography>
      </Box>

      {/* Mobile: icon-only trigger. */}
      <IconButton
        onClick={() => setOpen(true)}
        aria-label="Open command palette"
        sx={{ display: { xs: 'inline-flex', md: 'none' } }}
      >
        <SearchIcon />
      </IconButton>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        slotProps={{ paper: { sx: { position: 'fixed', top: 80, m: 0 } } }}
      >
        <Box sx={{ p: 2 }} onKeyDown={onListKeyDown}>
          <TextField
            autoFocus
            placeholder="Jump to…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
          <List sx={{ mt: 1 }}>
            {results.map((item, index) => {
              const Icon = item.icon;
              return (
                <ListItemButton
                  key={item.path}
                  selected={index === active}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => commit(index)}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              );
            })}
            {results.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 3 }}>
                No matches for “{query}”.
              </Typography>
            )}
          </List>
        </Box>
      </Dialog>
    </>
  );
}
